import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Feather, Quote } from 'lucide-react';

const AUTHORS = [
  {
    name: "Ernest Hemingway",
    image: "/hemingway-bg.jpg",
    quote: "There is nothing to writing. All you do is sit down and bleed.",
    subquote: "Start your masterpiece today",
    color: "from-amber-600 to-orange-700",
    textColor: "text-amber-100"
  },
  {
    name: "Virginia Woolf",
    image: "/virginia-woolf-bg.jpg",
    quote: "You cannot find peace by avoiding life.",
    subquote: "Let your story find its voice",
    color: "from-purple-600 to-indigo-700",
    textColor: "text-purple-100"
  },
  {
    name: "Mark Twain",
    image: "https://images.unsplash.com/photo-1765969934422-077ccae29246?w=1920&q=80&fit=crop",
    quote: "The secret of getting ahead is getting started.",
    subquote: "Your journey begins with one word",
    color: "from-blue-600 to-cyan-700",
    textColor: "text-blue-100"
  },
  {
    name: "Oscar Wilde",
    image: "/oscar-wilde-bg.jpg",
    quote: "Be yourself; everyone else is already taken.",
    subquote: "Write your unique story",
    color: "from-emerald-600 to-teal-700",
    textColor: "text-emerald-100"
  },
  {
    name: "Edgar Allan Poe",
    image: "/edgar-poe-bg.jpg",
    quote: "I became insane, with long intervals of horrible sanity.",
    subquote: "Unleash your darkest imagination",
    color: "from-slate-700 to-zinc-800",
    textColor: "text-slate-200"
  },
  {
    name: "Jane Austen",
    image: "/jane-austen-bg.jpg",
    quote: "It is a truth universally acknowledged that a story must be told.",
    subquote: "Craft your timeless narrative",
    color: "from-rose-500 to-pink-700",
    textColor: "text-rose-100"
  },
  {
    name: "Charles Dickens",
    image: "/charles-dickens-bg.jpg",
    quote: "There is nothing in the world so irresistibly contagious as laughter and good humor.",
    subquote: "Create characters that live forever",
    color: "from-yellow-600 to-amber-700",
    textColor: "text-yellow-100"
  }
];

export function HeroAuthorSlider({ onCtaClick }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  // Auto-rotate every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 6000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % AUTHORS.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + AUTHORS.length) % AUTHORS.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  const current = AUTHORS[currentIndex];

  return (
    <div className="relative w-full h-[500px] md:h-[600px] rounded-2xl overflow-hidden shadow-2xl mb-12">
      {/* Background Gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${current.color} transition-all duration-700`} />
      
      {/* Author Image */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-full h-full">
          {/* Image with overlay */}
          {!imageErrors[currentIndex] ? (
            <img
              src={current.image}
              alt={current.name}
              className={`absolute inset-0 w-full h-full object-cover opacity-30 transition-opacity duration-700 ${isTransitioning ? 'opacity-0' : 'opacity-30'}`}
              onError={() => handleImageError(currentIndex)}
            />
          ) : null}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        </div>
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-center px-8 md:px-16 z-10">
        <div className={`max-w-3xl transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          {/* Quote Icon */}
          <Quote className="w-12 h-12 text-white/40 mb-4" />
          
          {/* Main Quote */}
          <blockquote className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
            "{current.quote}"
          </blockquote>
          
          {/* Author Name */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/30 bg-white/10">
              {!imageErrors[currentIndex] ? (
                <img
                  src={current.image}
                  alt={current.name}
                  className="w-full h-full object-cover"
                  onError={() => handleImageError(currentIndex)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold">
                  {current.name.charAt(0)}
                </div>
              )}
            </div>
            <div>
              <p className="text-xl md:text-2xl font-semibold text-white">{current.name}</p>
              <p className={`text-lg ${current.textColor}`}>{current.subquote}</p>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={onCtaClick}
            className="mt-6 inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
          >
            <Feather className="w-5 h-5" />
            Start Writing Your Story
          </button>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all z-20"
        aria-label="Previous author"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all z-20"
        aria-label="Next author"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {AUTHORS.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentIndex 
                ? 'bg-white w-8' 
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Author Counter */}
      <div className="absolute top-6 right-6 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm z-20">
        {currentIndex + 1} / {AUTHORS.length}
      </div>
    </div>
  );
}

export default HeroAuthorSlider;
